import * as React from "react";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Collapse from "@mui/material/Collapse";
import { BsGrid, BsBoxSeam, BsClipboardData, BsChevronDown } from "react-icons/bs";
import { FiPlusSquare, FiImage, FiLogOut, FiSettings, FiUsers } from "react-icons/fi";
import { BiPackage } from "react-icons/bi";
import { MdInventory } from "react-icons/md";
import { useNavigate } from "react-router-dom";

const menuSections = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    items: [
      { icon: <BsGrid size={24} />, label: 'Overview', path: '/' }
    ]
  },
  {
    id: 'products',
    label: 'Products',
    items: [
      { icon: <FiPlusSquare size={24} />, label: 'Add New', path: '/addnew' },
      { icon: <MdInventory size={24} />, label: 'Inventory', path: '/inventory' }
    ]
  },
  {
    id: 'orders',
    label: 'Orders',
    items: [
      { icon: <BsBoxSeam size={24} />, label: 'View Orders', path: '/orders' },
      { icon: <BiPackage size={24} />, label: 'Track Orders', path: '/trackawbs' }
    ]
  },
  {
    id: 'analytics',
    label: 'Analytics',
    items: [
      { icon: <BsClipboardData size={24} />, label: 'Statistics', path: '/statistics' },
      { icon: <FiUsers size={24} />, label: 'Customers', path: '/customers' }
    ]
  },
  {
    id: 'settings',
    label: 'Settings',
    items: [
      { icon: <FiImage size={24} />, label: 'Banners', path: '/banners' },
      { icon: <FiSettings size={24} />, label: 'System', path: '/settings' }
    ]
  }
];

export default function SideDrawer({ name, profilePic, open, setOpen }) {
  const navigate = useNavigate();
  const [expandedSections, setExpandedSections] = React.useState({
    products: true,
    orders: true,
    analytics: true,
    settings: true
  });

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const handleNavigation = (path) => {
    navigate(path);
    setOpen(false);
  };

  const handleLogout = () => {
    navigate("/login");
    setOpen(false);
  };

  const DrawerList = (
    <Box sx={{ width: 300 }} role="presentation" className="h-full bg-white">
      <div className="p-4">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold">Navigation</h2>
          <button
            onClick={() => setOpen(false)}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <BsChevronDown className="w-5 h-5 transform rotate-90" />
          </button>
        </div>

        <div className="flex flex-col items-center mb-6">
          <img
            src={profilePic}
            alt="avatar"
            className="w-16 h-16 rounded-full border-2 border-gray-200"
          />
          <h2 className="mt-2 text-lg font-medium">{name}</h2>
        </div>

        <div className="space-y-2">
          {menuSections.map((section) => (
            <div key={section.id} className="mb-2">
              <button
                onClick={() => toggleSection(section.id)}
                className="flex items-center justify-between w-full px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900"
              >
                <span>{section.label}</span>
                <BsChevronDown
                  className={`w-4 h-4 transition-transform ${
                    expandedSections[section.id] ? 'transform rotate-180' : ''
                  }`}
                />
              </button>
              
              <Collapse in={expandedSections[section.id]}>
                <List className="py-0">
                  {section.items.map((item, index) => (
                    <ListItem
                      key={index}
                      disablePadding
                      onClick={() => handleNavigation(item.path)}
                    >
                      <ListItemButton className="py-2">
                        <ListItemIcon className="min-w-0 mr-3">
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{
                            className: "text-sm font-medium"
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </div>
          ))}
        </div>

        <Divider className="my-4" />

        <ListItem
          button
          onClick={handleLogout}
          className="text-red-600 hover:bg-red-50 rounded-lg"
        >
          <ListItemIcon className="min-w-0 mr-3">
            <FiLogOut size={24} />
          </ListItemIcon>
          <ListItemText
            primary="Logout"
            primaryTypographyProps={{
              className: "text-sm font-medium"
            }}
          />
        </ListItem>
      </div>
    </Box>
  );

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={() => setOpen(false)}
      PaperProps={{
        className: "bg-white"
      }}
    >
      {DrawerList}
    </Drawer>
  );
}
